/**
 * DeploymentsView Component
 * 
 * Displays deployment history with expandable release cards.
 * Similar to GitHub commits view.
 * 
 * Features:
 * - List of releases with current deployment indicator
 * - Expandable cards to view file changes
 * - File viewer modal with syntax highlighting
 * - File diff modal with side-by-side comparison
 * - Infinite scroll pagination (load more)
 */

import { useState } from 'react';
import { useReleases, FileDiffData } from '../../../../hooks/useReleases';
import { FileChangeNode } from '../../../../types/AppPanel';
import { NEW_PARSE_DASHBOARD_URL } from '../../../../settings';
import ReleaseCard from './ReleaseCard';
import FileViewerModal from './FileViewerModal';
import FileDiffModal from './FileDiffModal';

interface DeploymentsViewProps {
  /** Dashboard App ID (UUID format) */
  appId?: string;
  /** URL to open full dashboard */
  dashboardUrl?: string;
  /** Signal to trigger data refresh (incremented externally) */
  refreshSignal?: number;
}

export default function DeploymentsView({
  appId,
  dashboardUrl,
  refreshSignal,
}: DeploymentsViewProps) {
  const {
    releases,
    currentDeployment,
    isLoading,
    error,
    hasMore,
    loadMore,
    expandedReleaseId,
    releaseDetail,
    isLoadingDetail,
    toggleRelease,
    fetchFileContent,
    fetchFileDiff,
  } = useReleases(appId, refreshSignal);

  // File viewer modal state
  const [viewingFile, setViewingFile] = useState<{
    node: FileChangeNode;
    releaseId: number;
    content: string | null;
    isLoading: boolean;
  } | null>(null);

  // File diff modal state
  const [viewingDiff, setViewingDiff] = useState<{
    diffData: FileDiffData | null;
    isLoading: boolean;
  } | null>(null);

  /**
   * Handle file click - opens modal and fetches content
   */
  const handleViewFile = async (node: FileChangeNode, releaseId: number) => {
    if (!node.data) return;

    // Open modal with loading state
    setViewingFile({
      node,
      releaseId,
      content: null,
      isLoading: true,
    });

    // Fetch content
    const content = await fetchFileContent(releaseId, node.data);
    
    // Update modal with content
    setViewingFile(prev => prev ? {
      ...prev,
      content,
      isLoading: false,
    } : null);
  };

  /**
   * Handle diff click - opens diff modal and fetches both versions
   */
  const handleViewDiff = async (node: FileChangeNode, releaseId: number) => {
    if (!node.data) return;

    // Open modal with loading state
    setViewingDiff({
      diffData: null,
      isLoading: true,
    });

    // Fetch diff data (both versions)
    const diffData = await fetchFileDiff(releaseId, node);
    
    // Update modal with diff data
    setViewingDiff({
      diffData,
      isLoading: false,
    });
  };

  // Loading state (initial load)
  if (isLoading && releases.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex items-center gap-3 text-white/50">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span>Loading deployments...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 gap-4">
        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="text-red-400">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
        <p className="text-white/60 text-sm text-center">{error}</p>
        <p className="text-white/40 text-xs">Make sure you have deployments for this app</p>
      </div>
    );
  }

  // Empty state
  if (releases.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 gap-4">
        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="text-white/30">
            <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2"/>
            <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </div>
        <p className="text-white/40 text-sm text-center">
          No deployments yet.<br />
          Deploy your app to see the history here.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          {/* History/Timeline icon for deployment history */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-white/50">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 12h2M19 12h2M12 3v2M12 19v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
          </svg>
          <span className="text-sm font-medium text-white/70">Deployment History</span>
          <span className="text-xs text-white/40 bg-white/5 px-2 py-0.5 rounded-full">
            {releases.length}{hasMore ? '+' : ''} releases
          </span>
        </div>

        {appId && (
          <a
            href={`${NEW_PARSE_DASHBOARD_URL}/apps/${appId}/deployments`}
            target="_blank"
            rel="noreferrer noopener"
            className="
              flex items-center gap-1.5 px-3 py-1.5 rounded-lg
              text-xs text-white/70 hover:text-white
              bg-white/5 hover:bg-white/10 border border-white/10
              transition-colors duration-200
            "
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11"/>
              <path d="M15 3H21V9"/>
              <path d="M10 14L21 3"/>
            </svg>
            View in Dashboard
          </a>
        )}
      </div>

      {/* Releases List */}
      <div className="flex-1 overflow-auto p-4 space-y-3">
        {releases.map((release) => (
          <ReleaseCard
            key={release._id}
            release={release}
            isCurrent={currentDeployment?.releaseId === release.releaseId}
            isExpanded={expandedReleaseId === release.releaseId}
            isLoadingDetail={isLoadingDetail && expandedReleaseId === release.releaseId}
            detail={expandedReleaseId === release.releaseId ? releaseDetail : null}
            onToggle={() => toggleRelease(release.releaseId)}
            onViewFile={(node) => handleViewFile(node, release.releaseId)}
            onViewDiff={(node) => handleViewDiff(node, release.releaseId)}
          />
        ))}

        {/* Load More Button */}
        {hasMore && (
          <button
            onClick={loadMore}
            disabled={isLoading}
            className="
              w-full py-3 text-sm text-white/50 hover:text-white/70
              bg-white/5 hover:bg-white/10 rounded-lg
              border border-white/10 border-dashed
              transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Loading...
              </span>
            ) : (
              'Load more releases'
            )}
          </button>
        )}
      </div>

      {/* File Viewer Modal */}
      {viewingFile && (
        <FileViewerModal
          node={viewingFile.node}
          content={viewingFile.content}
          isLoading={viewingFile.isLoading}
          onClose={() => setViewingFile(null)}
        />
      )}

      {/* File Diff Modal */}
      {viewingDiff && viewingDiff.diffData && (
        <FileDiffModal
          diffData={viewingDiff.diffData}
          isLoading={viewingDiff.isLoading}
          onClose={() => setViewingDiff(null)}
        />
      )}

      {/* Diff Loading Overlay */}
      {viewingDiff && viewingDiff.isLoading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="flex items-center gap-3 text-white/70 bg-[#1e1e1e] px-6 py-4 rounded-lg border border-white/10">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span>Loading diff...</span>
          </div>
        </div>
      )}
    </div>
  );
}
